import { useReportDateState } from "../hooks/useReportDateAccess";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { toLocalYmd } from "../utils/localDate";
import styled from "styled-components";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { FaSyncAlt } from "react-icons/fa";
import { downloadCSV, downloadExcel, downloadPDF, printThermal, printThermalViaUsb} from "../utils/exportHelpers";
import { formatStoredMoney } from "../utils/currency";
import ReportPageHeader from "../components/ReportPageHeader";
import ReportPeriodPicker from "../components/ReportPeriodPicker";
import ReportControlBar from "../components/ReportControlBar";
import { API_BASE_URL } from "../config/constants";
import { getOfflinePaymentMethodReport } from "../offline/financeStore";
import { isFullOfflineModeEnabled } from "../utils/fullOfflineMode";
import { buildItemSalesReceiptText } from "../utils/categorySalesReport";
import {
  isDesktopApp,
  indiaDateKey,
  numberValue,
  extractOrderItems,
  getCachedOrderDetail,
  DESKTOP_CACHE_HEADER,
} from "../utils/offlineReportUtils";

const DEFAULT_REPORT = (date) => ({
  date,
  restaurant: "",
  total_orders: 0,
  total_quantity: 0,
  total_amount: 0,
  total_credit_amount: 0,
  modes: [],
});

const PaymentMethodReportPage = () => {
  const [userId, setUserId] = useState(null);
  const [date, setDate] = useReportDateState(today());
  const [report, setReport] = useState(DEFAULT_REPORT(today()));
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [, setSourceMessage] = useState("");
  const reportRef = useRef(null);
  const storedUser = useMemo(() => {
    try { return JSON.parse(localStorage.getItem("userData") || "{}")?.data || {}; }
    catch { return {}; }
  }, []);

  useEffect(() => {
    const user = JSON.parse(localStorage.getItem("userData") || "{}").data;
    if (!user?.id) {
      toast.error("User not found. Please login again.");
      setLoading(false);
      return;
    }
    setUserId(user.id);
  }, []);

  useEffect(() => {
    if (userId) fetchReport();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, date]);

  const fetchReport = async (opts = {}) => {
    if (!userId) return;
    opts.refresh ? setRefreshing(true) : setLoading(true);
    setSourceMessage("");
    try {
      if (isFullOfflineModeEnabled()) {
        const offlineReport = await getOfflinePaymentMethodReport(userId, date);
        setReport({
          ...DEFAULT_REPORT(date),
          ...offlineReport,
          modes: Array.isArray(offlineReport?.modes) ? offlineReport.modes : [],
        });
        setSourceMessage(offlineSourceMessage(offlineReport));
        return;
      }
      const res = await fetch(
        `${API_BASE_URL}/report-v2/payment-methods`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ restaurantid: userId, date }),
        }
      );
      const json = await res.json();
      if (!res.ok || !json?.success) {
        const offlineReport = await buildOfflinePaymentMethodReport(userId, date);
        if (!offlineReport) throw new Error(json?.message || "Failed to load report");
        setReport(offlineReport);
        setSourceMessage(offlineSourceMessage(offlineReport));
        return;
      }
      
      const liveOrCachedReport = { ...DEFAULT_REPORT(date), ...json, modes: Array.isArray(json?.modes) ? json.modes : [] };
      
      if (res.headers.get(DESKTOP_CACHE_HEADER) === "1") {
        const offlineReport = await buildOfflinePaymentMethodReport(userId, date);
        if (offlineReport?.modes?.length || !liveOrCachedReport.modes.length) {
          setReport(offlineReport || liveOrCachedReport);
          setSourceMessage(
            offlineReport
              ? offlineSourceMessage(offlineReport)
              : "Offline mode — showing the last saved payment report for this date."
          );
        } else {
          setReport(liveOrCachedReport);
          setSourceMessage("Offline mode — showing the last saved payment report for this date.");
        }
      } else {
        setReport(liveOrCachedReport);
      }
    } catch (e) {
      try {
        const offlineReport = await buildOfflinePaymentMethodReport(userId, date);
        if (offlineReport) {
          setReport(offlineReport);
          setSourceMessage(offlineSourceMessage(offlineReport));
        } else {
          toast.error(e.message || "Unable to load report");
          setReport(DEFAULT_REPORT(date));
        }
      } catch (offlineError) {
        console.warn("Unable to build offline payment method report", offlineError);
        toast.error(e.message || "Unable to load report");
        setReport(DEFAULT_REPORT(date));
      }
    } finally {
      opts.refresh ? setRefreshing(false) : setLoading(false);
    }
  };

  const fmtINR = (n) => formatStoredMoney(n, { minimumFractionDigits: 0, maximumFractionDigits: 2 });

  const normalizedModes = useMemo(() => {
    const base = {
      cash: { label: "Cash" },
      card: { label: "Card" },
      upi: { label: "UPI" },
      credit: { label: "Credit" },
    };
    const incoming = report?.modes || [];
    const map = {};
    incoming.forEach((m) => {
      const key = String(m.mode || "").toLowerCase();
      map[key] = {
        key,
        label: m.mode || base[key]?.label || "Other",
        total_orders: Number(m.total_orders || 0),
        total_quantity: Number(m.total_quantity || 0),
        total_amount: Number(m.total_amount || 0),
        share: Number(m.share || 0),
      };
    });
    return Object.keys(base).map((key) => ({
      key,
      label: base[key].label,
      total_orders: map[key]?.total_orders || 0,
      total_quantity: map[key]?.total_quantity || 0,
      total_amount: map[key]?.total_amount || 0,
      share: map[key]?.share || 0,
    }));
  }, [report]);

  const exportCsv = () => {
    const rows = normalizedModes.map((m, idx) => [
      idx + 1,
      m.label,
      m.total_orders,
      m.total_quantity,
      m.total_amount,
      `${m.share}%`,
    ]);
    downloadCSV(
      `payment_methods_${date}.csv`,
      ["#", "Payment Mode", "Orders", "Qty", "Amount", "Share"],
      rows
    );
  };

  const exportExcel = () => {
    const rows = normalizedModes.map((m, idx) => [
      idx + 1,
      m.label,
      m.total_orders,
      m.total_quantity,
      m.total_amount,
      `${m.share}%`,
    ]);
    downloadExcel(
      `payment_methods_${date}.xlsx`,
      ["#", "Payment Mode", "Orders", "Qty", "Amount", "Share"],
      rows
    );
  };

  const exportPdf = () => downloadPDF(reportRef.current, `payment_methods_${date}.pdf`);

  const printableModes = useMemo(
    () => normalizedModes.map((mode) => ({
      item_name: `${mode.label} (${mode.total_orders} orders)`,
      total_quantity: mode.total_quantity,
      total_amount: mode.total_amount,
      total_orders: mode.total_orders,
    })),
    [normalizedModes]
  );

  const buildThermalReceipt = (lineWidth) =>
    buildItemSalesReceiptText(
      {
        title: "Payment Method Report",
        restaurant:
          report.restaurant ||
          storedUser.restaurant_name ||
          storedUser.name ||
          "Bill Sarthi",
        addressLines: [
          storedUser.addressline1 || storedUser.address || "",
          storedUser.addressline2 || "",
          storedUser.addressline3 || "",
        ],
        registrationLines: [
          storedUser.customfield1 || "",
          storedUser.customfield2 || "",
          storedUser.customfield3 || "",
          storedUser.gstno ? `GSTIN: ${storedUser.gstno}` : "",
        ],
        periodLabel: `From ${displayDate(date)} to ${displayDate(date)}`,
        printedAt: new Date(),
        items: printableModes,
      },
      lineWidth
    );

  return (
    <Page>
      <ToastContainer position="top-right" autoClose={2200} />

      <ReportPageHeader
        title="Payment Method Report"
        subtitle="Sales breakdown by cash, card, UPI, and credit."
        stats={[
          { label: "Total Orders", value: report.total_orders || 0 },
          { label: "Total Qty", value: report.total_quantity || 0 },
          { label: "Total Sales", value: fmtINR(report.total_amount) },
          { label: "Total Credit", value: fmtINR(report.total_credit_amount) },
        ]}
      />

      <ReportControlBar>
        <ReportPeriodPicker date={date} onDateChange={(d) => setDate(d)} />
        <PrimaryBtn onClick={() => fetchReport({ refresh: true })}>
          <FaSyncAlt /> {refreshing ? "Refreshing..." : "Refresh"}
        </PrimaryBtn>
      </ReportControlBar>


      <Card ref={reportRef}>
        {loading ? (
          <Center>Loading…</Center>
        ) : normalizedModes.length === 0 ? (
          <Center>No data for this date.</Center>
        ) : (
          <TableWrap>
            <Table>
              <thead>
                <tr>
                  <th>Particulars</th>
                  <th>Qty</th>
                  <th>Amount</th>
                </tr>
              </thead>
              <tbody>
                {normalizedModes.map((m) => (
                  <tr key={m.key}>
                    <td>
                      <ModeName>{m.label}</ModeName>
                      <ModeMeta>{m.total_orders} orders · {m.share}% share</ModeMeta>
                    </td>
                    <NumberCell>{m.total_quantity}</NumberCell>
                    <NumberCell>{fmtINR(m.total_amount)}</NumberCell>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <GrandTotalRow>
                  <td>Grand Total</td>
                  <NumberCell>{report.total_quantity || 0}</NumberCell>
                  <NumberCell>{fmtINR(report.total_amount)}</NumberCell>
                </GrandTotalRow>
              </tfoot>
            </Table>
          </TableWrap>
        )}

        <ActionsRow style={{ marginTop: 14 }}>
          <PrimaryBtn onClick={exportExcel}>Excel</PrimaryBtn>
          <PrimaryBtn onClick={exportPdf}>PDF</PrimaryBtn>
          <PrimaryBtn onClick={() => printThermal(
            reportRef.current,
            "Payment Method Report",
            { buildReceiptText: buildThermalReceipt }
          )}>
            Thermal Print
          </PrimaryBtn><PrimaryBtn onClick={() => printThermalViaUsb(
            reportRef.current,
            "Payment Method Report",
            { buildReceiptText: buildThermalReceipt }
          )}>
            Thermal Print via USB
          </PrimaryBtn>
          <PrimaryBtn onClick={exportCsv}>CSV</PrimaryBtn>
        </ActionsRow>
      </Card>
    </Page>
  );
};

export default PaymentMethodReportPage;

const today = () => toISO(new Date());
const toISO = (d) => toLocalYmd(d);
const displayDate = (value) => {
  const [year, month, day] = String(value || "").split("-");
  return year && month && day ? `${day}-${month}-${year}` : String(value || "");
};

const Page = styled.div`
  background: radial-gradient(circle at 20% 20%, #f5f8ff, #eef2ff 55%, #f5f7fb 100%);
  min-height: 100vh;
  padding: 18px;
`;


const getPaymentMode = (o) => {
  const raw = String(o?.mode ?? o?.payment_mode ?? o?.paymentmode ?? o?.paymenttype ?? o?.payment_type ?? "cash").toLowerCase();
  if (raw.includes("cash")) return "cash";
  if (raw.includes("card")) return "card";
  if (raw.includes("upi") || raw.includes("online") || raw.includes("gpay") || raw.includes("phonepe") || raw.includes("paytm")) return "upi";
  if (raw.includes("credit") || raw.includes("udhar") || raw.includes("khata") || raw.includes("pending")) return "credit";
  return raw || "cash";
};

const buildOfflinePaymentMethodReport = async (restaurantId, selectedDate) => {
  if (!isDesktopApp()) return null;
  const [cachedOrders, pendingRows] = await Promise.all([
    window.desktopOrders.list({ restaurantId }),
    window.desktopOrderQueue?.list({ restaurantId, includeDeferred: true }) || [],
  ]);

  const orders = (cachedOrders || []).filter((order) =>
    indiaDateKey(order?.created_at ?? order?.createdAt ?? order?.date) === selectedDate
  );
  const pendingOrders = (pendingRows || [])
    .filter((entry) => indiaDateKey(entry?.createdAt) === selectedDate)
    .map((entry) => ({
      ...entry.payload,
      is_pending_sync: true,
    }));

  const allOrders = [...pendingOrders, ...orders];

  let total_orders = 0;
  let total_quantity = 0;
  let total_amount = 0;
  let total_credit_amount = 0;
  const modeMap = {};

  await Promise.all(allOrders.map(async (order) => {
    let orderQty = numberValue(order?.total_items ?? order?.totalquantity ?? order?.qty);
    if (!orderQty) {
      const detail = order?.is_pending_sync ? null : await getCachedOrderDetail(restaurantId, order);
      const items = extractOrderItems(detail, order);
      orderQty = items.reduce((sum, item) => sum + numberValue(item?.quantity ?? item?.qty ?? item?.total_quantity ?? 1), 0);
    }
    
    const amount = numberValue(order?.grand_total ?? order?.grandtotal ?? order?.totalamount ?? order?.total_amount ?? order?.amount ?? order?.total);
    const mode = getPaymentMode(order);

    total_orders += 1;
    total_quantity += orderQty;
    total_amount += amount;

    if (mode === "credit") {
      total_credit_amount += amount;
    }

    if (!modeMap[mode]) {
      modeMap[mode] = { mode, total_orders: 0, total_quantity: 0, total_amount: 0, share: 0 };
    }
    modeMap[mode].total_orders += 1;
    modeMap[mode].total_quantity += orderQty;
    modeMap[mode].total_amount += amount;
  }));

  const modes = Object.values(modeMap).map(m => {
    m.share = total_amount > 0 ? Number(((m.total_amount / total_amount) * 100).toFixed(2)) : 0;
    return m;
  });

  return {
    ...DEFAULT_REPORT(selectedDate),
    success: true,
    offline: true,
    total_orders,
    total_quantity,
    total_amount,
    total_credit_amount,
    modes,
  };
};

const offlineSourceMessage = (report = {}) => {
  const missing = numberValue(report?.missing_detail_count);
  if (report?.full_offline && missing > 0) {
    return `Full Offline — payment totals are local; ${missing} saved sale${missing === 1 ? " has" : "s have"} no item details, so its quantity is unavailable.`;
  }
  if (report?.full_offline) {
    return "Full Offline — payment method totals are calculated only from the isolated local database.";
  }
  return "Offline mode — payment method totals calculated from range-sync SQLite bills and pending orders.";
};

const ActionsRow = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
  flex-wrap: wrap;
`;

const PrimaryBtn = styled.button`
  background: linear-gradient(135deg, var(--app-primary), var(--app-primary-hover));
  color: #fff;
  border: 0;
  border-radius: 10px;
  padding: 8px 14px;
  font-weight: 700;
  font-size: 0.8rem;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  box-shadow: 0 4px 12px rgba(7, 95, 158, 0.15);
  transition: all 0.15s ease;

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 6px 16px rgba(7, 95, 158, 0.25);
  }
`;

const Card = styled.div`
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 18px;
  padding: 18px;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.05);
`;

const Center = styled.div`
  text-align: center;
  color: #64748b;
  padding: 24px 0;
  font-weight: 600;
`;

const TableWrap = styled.div`
  overflow-x: auto;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  th,
  td {
    padding: 12px 14px;
    border-bottom: 1px solid #e2e8f0;
    text-align: left;
    font-size: 14px;
  }
  th {
    background: #f8fafc;
    color: #334155;
    font-weight: 800;
    text-transform: uppercase;
    font-size: 0.75rem;
    letter-spacing: 0.5px;
  }
  tr:hover td {
    background: #f8fafc;
  }
`;

const ModeName = styled.div`
  color: #1f2937;
  font-weight: 800;
`;

const ModeMeta = styled.div`
  margin-top: 2px;
  color: #64748b;
  font-size: 12px;
  font-weight: 700;
`;

const NumberCell = styled.td`
  text-align: right !important;
  font-variant-numeric: tabular-nums;
`;

const GrandTotalRow = styled.tr`
  td {
    background: #e8f3fc;
    color: #0f3f61;
    font-weight: 900;
    border-top: 2px solid var(--app-primary);
  }
`;
